import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import notify from "devextreme/ui/notify";
import { productosService } from "../../services/productosService";
import { Productos } from "../../dtos/Productos";
import { StyledInput } from "./TablaActivos.styles";

export const formatProducto = (item: Productos | null | undefined): string => {
  if (!item?.codigo) return "";
  const desc = item.descripcion?.trim();
  return desc ? `${item.codigo} - ${desc}` : String(item.codigo);
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: stretch;
`;

const SearchButton = styled.button`
  flex-shrink: 0;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  background: #fff;
  color: #0052cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f4f8ff;
    border-color: #0052cc;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResultsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  box-shadow: 0 8px 16px -4px rgba(9, 30, 66, 0.15);
  max-height: 220px;
  overflow-y: auto;
`;

const ResultItem = styled.li`
  padding: 10px 12px;
  font-size: 14px;
  color: #172b4d;
  cursor: pointer;

  &:hover {
    background: #f4f8ff;
  }
`;

const EmptyResult = styled.li`
  padding: 10px 12px;
  font-size: 13px;
  color: #6b778c;
`;

type ProductoAutocompleteProps = {
  value: number | null;
  onChange: (codigo: number | null) => void;
  disabled?: boolean;
};

const ProductoAutocomplete: React.FC<ProductoAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState("");
  const [results, setResults] = useState<Productos[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const selectedCodigoRef = useRef<number | null>(null);

  const syncFromCodigo = useCallback(async (codigo: number) => {
    try {
      const producto = await productosService.getByCodigo(codigo);
      const label = formatProducto(producto);
      selectedCodigoRef.current = codigo;
      setInputText(label);
    } catch {
      selectedCodigoRef.current = null;
      setInputText(String(codigo));
    }
  }, []);

  useEffect(() => {
    if (value == null) {
      selectedCodigoRef.current = null;
      setInputText("");
      setResults([]);
      setOpen(false);
      return;
    }
    if (selectedCodigoRef.current !== value) {
      syncFromCodigo(value);
    }
  }, [value, syncFromCodigo]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const runSearch = async () => {
    const term = inputText.trim();
    if (!term) {
      notify("Escribe un código o descripción para buscar.", "warning", 2000);
      return;
    }

    setLoading(true);
    try {
      const items = await productosService.search(term);
      setResults(items);
      setOpen(true);
      if (items.length === 0) {
        notify("No se encontraron productos.", "info", 2000);
      }
    } catch {
      setResults([]);
      setOpen(true);
      notify("Error al buscar productos.", "error", 2500);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (producto: Productos) => {
    const label = formatProducto(producto);
    selectedCodigoRef.current = producto.codigo;
    setInputText(label);
    onChange(producto.codigo ?? null);
    setOpen(false);
    setResults([]);
  };

  const handleInputChange = (text: string) => {
    setInputText(text);
    if (selectedCodigoRef.current != null) {
      selectedCodigoRef.current = null;
      onChange(null);
    }
    setOpen(false);
    setResults([]);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <InputRow>
        <StyledInput
          value={inputText}
          disabled={disabled}
          placeholder="Código o descripción..."
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runSearch();
            }
          }}
        />
        <SearchButton type="button" disabled={disabled || loading} onClick={runSearch}>
          {loading ? "..." : "Buscar"}
        </SearchButton>
      </InputRow>

      {open && (
        <ResultsList>
          {results.length === 0 ? (
            <EmptyResult>Sin resultados</EmptyResult>
          ) : (
            results.map((producto) => (
              <ResultItem
                key={producto.codigo ?? producto.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(producto)}
              >
                {formatProducto(producto)}
              </ResultItem>
            ))
          )}
        </ResultsList>
      )}
    </Wrapper>
  );
};

export default ProductoAutocomplete;
